import TimelineStep from "./TimelineStep";
import moment from "moment";

const PACKING_ORDER_STATUS = {
    CREATED: 1,
    PICKED: 2,
    PACKED: 3,
    LABEL_PRINTED: 7,
    SHIPPED: 4,
    CANCELED: 5
};

const TRANSPORT_ORDER_STATUS = {
    TO_BE_PICKED_UP: 1,
    DISPATCHED: 2,
    IN_TRANSIT: 3,
    DELIVERED: 4,
    PARTIALLY_DELIVERED: 5,
    DELAYED: 6,
    CANCELED: 7,
    CLIENT_NOT_IN_ADDRESS: 8,
    SECOND_DELIVERY_ATTEMPT: 9,
    RETURN_TO_ORIGIN: 10,
    RETURNED_TO_ORIGIN: 11
};

const calculateTimelineStatus = (order) => {
    const timeline = {
        ordenRecibida: { completed: false, inProgress: false, canceled: false },
        empaquetado: { completed: false, inProgress: false, canceled: false },
        despachado: { completed: false, inProgress: false, canceled: false },
        enTransito: { completed: false, inProgress: false, canceled: false },
        entregado: { completed: false, inProgress: false, canceled: false },
        isCanceled: false,
        canceledAt: null
    };

    if (!order) {
        return timeline;
    }

    const packingStatus = order.status?.id;
    const transportOrder = order.transport_order;
    const transportStatus = transportOrder?.status?.id;
    const orderStatus = order?.status?.id;

    // Verificar si la orden está cancelada
    const isCanceled = packingStatus === PACKING_ORDER_STATUS.CANCELED;
    timeline.isCanceled = isCanceled;

    if (isCanceled) {
        timeline.ordenRecibida.completed = true;

        if (packingStatus >= 2) {
            timeline.empaquetado.canceled = true;
        }

        timeline.despachado.canceled = true;
        timeline.enTransito.canceled = true;
        timeline.entregado.canceled = true;

        if (packingStatus >= 2 && packingStatus < 3) {
            timeline.canceledAt = 'empaquetado';
        } else {
            timeline.canceledAt = 'ordenRecibida';
        }

        return timeline;
    }

    // Orden recibida
    if (packingStatus >= 1) {
        timeline.ordenRecibida.completed = true;
    }

    // Empaquetado
    if (packingStatus >= 3) {
        timeline.empaquetado.completed = true;
    } else if (packingStatus === 2) {
        timeline.empaquetado.inProgress = true;
    }

    // Despachado
    if (transportStatus >= 2) {
        timeline.despachado.completed = true;
    }

    // En tránsito
    if (transportStatus >= 3 || orderStatus === 99) {
        timeline.enTransito.completed = true;
    } else if (transportStatus === 2 || orderStatus === 99) {
        timeline.enTransito.inProgress = true;
    }

    // Entregado
    if (transportStatus === 4 || orderStatus === 8) {
        timeline.entregado.completed = true;
    }

    return timeline;
};

const OrderTimeline = ({ order }) => {
    const timeline = calculateTimelineStatus(order);

    const getStatus = (step) => {
        if (step.completed) return 'completed';
        if (step.inProgress) return 'inProgress';
        if (step.canceled) return 'canceled';
        return 'pending';
    };

    // Función para obtener el timestamp de un estado del packing order
    const getPackingStatusTimestamp = (statusId) => {
        const history = order?.status_history?.find(h => h.status?.id === statusId);
        return history?.created_at ? moment(history.created_at).format('DD/MM/YYYY - HH:mm[hs]') : null;
    };

    // Función para obtener el timestamp de un estado del transport order
    const getTransportStatusTimestamp = (statusId) => {
        const history = order?.transport_order?.status_history?.find(h => h.status?.id === statusId);
        return history?.created_at ? moment(history.created_at).format('DD/MM/YYYY - HH:mm[hs]') : null;
    };

    // Configuración de cada status para renderizado dinámico
    const getStatusStepConfig = (statusId) => {
        const configs = {
            [TRANSPORT_ORDER_STATUS.IN_TRANSIT]:              { icon: 'car',          title: 'En tránsito' },
            [TRANSPORT_ORDER_STATUS.PARTIALLY_DELIVERED]:     { icon: 'check-circle', title: 'Parcialmente entregado' },
            [TRANSPORT_ORDER_STATUS.DELAYED]:                 { icon: 'clock-circle', title: 'Demorado',               customColor: '#FF9500' },
            [TRANSPORT_ORDER_STATUS.CLIENT_NOT_IN_ADDRESS]:   { icon: 'close-circle', title: 'Cliente no encontrado',  customColor: '#f44336' },
            [TRANSPORT_ORDER_STATUS.SECOND_DELIVERY_ATTEMPT]: { icon: 'car',          title: 'Segunda vuelta' },
            [TRANSPORT_ORDER_STATUS.RETURN_TO_ORIGIN]:        { icon: 'rollback',     title: 'Volviendo a origen',     customColor: '#ff9800' },
            [TRANSPORT_ORDER_STATUS.RETURNED_TO_ORIGIN]:      { icon: 'home',         title: 'Devuelto al origen',     customColor: '#795548' },
        };
        return configs[statusId] || { icon: 'info-circle', title: 'Actualización de estado' };
    };

    // Construir sección dinámica del timeline a partir del historial de cambios
    const buildDynamicSteps = () => {
        const history = order?.transport_order?.status_history ?? [];
        const secondDeliveryAttempt = order?.transport_order?.second_delivery_attempt;

        // Filtrar entradas ya cubiertas por pasos fijos (Created, Dispatched)
        const entries = history.filter(h =>
            h.status?.id !== TRANSPORT_ORDER_STATUS.TO_BE_PICKED_UP &&
            h.status?.id !== TRANSPORT_ORDER_STATUS.DISPATCHED
        );

        const steps = [];

        entries.forEach((entry, idx) => {
            const isLast = idx === entries.length - 1;
            const driverName = entry.user
                ? `${entry.user.nombre || ''} ${entry.user.apellido || ''}`.trim()
                : '-';
            const entryStatus = isLast && entry.status?.id !== TRANSPORT_ORDER_STATUS.DELIVERED
                ? 'inProgress'
                : 'completed';

         
                // STATUS_CHANGE
                const config = getStatusStepConfig(entry.status?.id);

                const additionalInfo = entry.status?.id === TRANSPORT_ORDER_STATUS.SECOND_DELIVERY_ATTEMPT && secondDeliveryAttempt
                    ? (
                        <div>
                            <div>1er intento: {moment(secondDeliveryAttempt.first_attempt).format('D/MM/YY [a las] HH:mm [Hs.]')}</div>
                            <div style={{ marginTop: '4px' }}>{moment(secondDeliveryAttempt.second_attempt).format('DD/MM/YYYY - HH:mm[hs]')}</div>
                        </div>
                    ) : null;

                    if (isLast) {
                    const config = getStatusStepConfig(entry.status?.id);
                    steps.push(
                        <TimelineStep
                            key={`inherited-${entry.id}`}
                            icon={config.icon}
                            title={config.title}
                            timestamp={entry.created_at ? moment(entry.created_at).format('DD/MM/YYYY - HH:mm[hs]') : null}
                            description={`Responsable: ${driverName}`}
                            status="inProgress"
                            customColor={config.customColor}
                        />
                    );
                } else {
                    steps.push(
                        <TimelineStep
                            key={`event-${entry.id}`}
                            icon={config.icon}
                            title={config.title}
                            timestamp={entry.created_at ? moment(entry.created_at).format('DD/MM/YYYY - HH:mm[hs]') : null}
                            description={`Responsable: ${driverName}`}
                            additionalInfo={additionalInfo}
                            status={entryStatus}
                            customColor={config.customColor}
                        />
                    );
                }
            
        });

        return steps;
    };

    return (
        <div style={{ flex: 1, borderLeft: '1px solid #f0f0f0', paddingLeft: '60px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '40px', flexShrink: 0 }}>
                Timeline del pedido
            </h3>

            <div style={{ overflowY: 'auto', flex: 1, maxHeight: '70vh', paddingRight: '8px' }}>
            <div style={{ position: 'relative', paddingLeft: '60px' }}>
                {/* Línea vertical */}
                <div style={{
                    position: 'absolute',
                    left: '-32px',
                    top: '18px',
                    bottom: '18px',
                    width: '3px',
                    backgroundColor: timeline.isCanceled ? '#ef4444' : '#e0e0e0'
                }} />

                {/* Orden recibida */}
                <TimelineStep
                    icon="check"
                    title="Orden recibida"
                    timestamp={getPackingStatusTimestamp(PACKING_ORDER_STATUS.CREATED)}
                    description="Pedido ingresado al sistema"
                    status={getStatus(timeline.ordenRecibida)}
                />

                {/* Cancelado - después de orden recibida */}
                {timeline.isCanceled && timeline.canceledAt === 'ordenRecibida' && (
                    <TimelineStep
                        icon="close"
                        title="Cancelado"
                        timestamp={getPackingStatusTimestamp(PACKING_ORDER_STATUS.CANCELED)}
                        description="El pedido fue cancelado"
                        status="canceled"
                        isLast={true}
                    />
                )}

                {/* Empaquetado */}
                {!timeline.isCanceled && (
                    <TimelineStep
                        icon="check-circle"
                        title="Empaquetado"
                        timestamp={getPackingStatusTimestamp(PACKING_ORDER_STATUS.LABEL_PRINTED)}
                        description="Paquete preparado"
                        status={getStatus(timeline.empaquetado)}
                    />
                )}

                {/* Cancelado - durante empaquetado */}
                {timeline.isCanceled && timeline.canceledAt === 'empaquetado' && (
                    <TimelineStep
                        icon="close"
                        title="Cancelado"
                        timestamp={getPackingStatusTimestamp(PACKING_ORDER_STATUS.CANCELED)}
                        description="El pedido fue cancelado durante el empaquetado"
                        status="canceled"
                        isLast={true}
                    />
                )}

                {/* Despachado */}
                {!timeline.isCanceled && (() => {
                    const dispatchedEntry = (order?.transport_order?.status_history ?? []).find(h => h.status?.id === TRANSPORT_ORDER_STATUS.DISPATCHED);
                    const dispatchDriver = dispatchedEntry?.user;
                    const dispatchDriverName = dispatchDriver ? `${dispatchDriver.nombre || ''} ${dispatchDriver.apellido || ''}`.trim() : order.transport_order?.user ? `${order.transport_order.user.nombre || ''} ${order.transport_order.user.apellido || ''}`.trim() : '-';
                    return (
                        <TimelineStep
                            icon="inbox"
                            title="Despachado"
                            timestamp={getTransportStatusTimestamp(TRANSPORT_ORDER_STATUS.DISPATCHED)}
                            description={`Repartidor: ${dispatchDriverName}`}
                            status={getStatus(timeline.despachado)}
                        />
                    );
                })()}

                {/* Sección dinámica: En tránsito, transferencias, segunda vuelta, etc. */}
                {!timeline.isCanceled && buildDynamicSteps()}

                {/* Entregado */}
                {!timeline.isCanceled && (
                    <TimelineStep
                        icon="check-circle"
                        title="Entregado"
                        timestamp={getTransportStatusTimestamp(TRANSPORT_ORDER_STATUS.DELIVERED)}
                        description={timeline.entregado.completed ? "Pedido entregado" : "Pendiente de entrega"}
                        status={getStatus(timeline.entregado)}
                        isLast={true}
                    />
                )}
            </div>
            </div>
        </div>
    );
};

export default OrderTimeline;
